/**
 * 案件 FB 画面の表示用の純関数。
 *
 * 値（種類・判断・根拠の種類）は API が正本。ここに置く表示名は「知っている値を読みやすく
 * する」だけで、知らない値はそのまま出す（選択肢を画面に写さない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business Display helpers for project feedback screens: labels, reference links, per-version objections and judgments, list filters
 * @graph-connects none
 */

import type {
  FeedbackJudgment,
  FeedbackObjection,
  FeedbackReference,
  ProjectFeedback,
  ProjectFeedbackRevision,
  ProjectFeedbackSummary,
  SkillMapLabelFields,
} from '../types/project-feedback.js';

/** FB の種類の表示名。 @graph-connects none */
const KIND_LABELS: Readonly<Record<string, string>> = {
  requirement: '要件定義',
  development: '実装',
};

/** 判断の表示名。 @graph-connects none */
const VERDICT_LABELS: Readonly<Record<string, string>> = {
  valid: '妥当',
  invalid: '妥当でない',
};

/** Backlog の課題キー。 @graph-connects none */
const ISSUE_KEY_PATTERN = /^[A-Z][A-Z0-9_]*-\d+$/;

/** `org/repo#123` か `repo#123`。 @graph-connects none */
const PULL_KEY_PATTERN = /^(?:([\w.-]+)\/)?([\w.-]+)#(\d+)$/;

/** org を書かない PR の既定 org。 @graph-connects none */
const DEFAULT_GITHUB_ORG = 'air-closet';

/**
 * FB の種類の表示名。知らない値はそのまま。
 * @graph-connects KIND_LABELS [references] 表示名
 */
export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

/**
 * 判断の表示名。知らない値はそのまま。
 * @graph-connects VERDICT_LABELS [references] 表示名
 */
export function verdictLabel(verdict: string): string {
  return VERDICT_LABELS[verdict] ?? verdict;
}

/**
 * 評価項目の表示名。項目名が引けなかった ID は skillMapId をそのまま出す。
 * @graph-connects none
 */
export function skillMapLabel(item: SkillMapLabelFields & { skillMapId: string }): string {
  const name = item.skillName;
  return typeof name === 'string' && name !== '' ? name : item.skillMapId;
}

/**
 * 項目名が引けているか（引けていなければ ID を等幅で出す）。
 * @graph-connects none
 */
export function hasSkillName(item: SkillMapLabelFields): boolean {
  return typeof item.skillName === 'string' && item.skillName !== '';
}

/**
 * 評価項目にカーソルを当てたときの補足（カテゴリと ID）。
 * @graph-connects none
 */
export function skillMapTitle(item: SkillMapLabelFields & { skillMapId: string }): string {
  return [item.category, item.skillMapId]
    .filter((part): part is string => typeof part === 'string' && part !== '')
    .join(' · ');
}

/**
 * 根拠を開くリンク先。URL・Backlog の課題キー・PR（`org/repo#n`）だけ組み立て、
 * それ以外は null（テキストだけ出す）。
 * @graph-connects none
 */
export function referenceHref(reference: FeedbackReference): string | null {
  const key = reference.key.trim();
  if (/^https?:\/\//.test(key)) return key;
  if (ISSUE_KEY_PATTERN.test(key)) return `https://air-closet.backlog.jp/view/${key}`;
  const pull = PULL_KEY_PATTERN.exec(key);
  if (pull !== null && (reference.type === 'pr' || reference.type === 'review')) {
    const [, org, repo, number] = pull;
    return `https://github.com/${org ?? DEFAULT_GITHUB_ORG}/${repo ?? ''}/pull/${number ?? ''}`;
  }
  return null;
}

/**
 * ある版・項目に付いた判断（版と項目の組ごとに 1 件）。
 * @graph-connects none
 */
export function judgmentFor(
  judgments: readonly FeedbackJudgment[],
  version: number,
  entryId: string,
): FeedbackJudgment | undefined {
  return judgments.find((j) => j.version === version && j.entryId === entryId);
}

/**
 * ある版・項目に出た異議（出した順）。
 * @graph-connects none
 */
export function objectionsFor(
  objections: readonly FeedbackObjection[],
  version: number,
  entryId: string,
): FeedbackObjection[] {
  return objections
    .filter((o) => o.version === version && o.entryId === entryId)
    .sort((a, b) => a.at.localeCompare(b.at));
}

/**
 * 表示する版の FB。指定が無い・見つからなければ最新版。
 * @graph-connects none
 */
export function feedbackAtVersion(
  latest: ProjectFeedback,
  revisions: readonly ProjectFeedbackRevision[],
  version: number | undefined,
): ProjectFeedback {
  if (version === undefined || version === latest.version) return latest;
  return revisions.find((r) => r.version === version)?.feedback ?? latest;
}

/** 一覧の絞り込み条件。 @graph-connects none */
export interface FeedbackListFilter {
  email?: string;
  pjKey?: string;
  hasObjection?: boolean;
}

/**
 * 一覧を絞り込む。
 * @graph-connects none
 */
export function filterFeedbacks(
  feedbacks: readonly ProjectFeedbackSummary[],
  filter: FeedbackListFilter,
): ProjectFeedbackSummary[] {
  return feedbacks.filter(
    (f) =>
      (filter.email === undefined || f.email === filter.email) &&
      (filter.pjKey === undefined || f.pjKey === filter.pjKey) &&
      (filter.hasObjection !== true || f.openObjectionCount > 0),
  );
}

/** 絞り込みの選択肢。 @graph-connects none */
export interface FilterOption {
  value: string;
  label: string;
}

/**
 * 一覧に出てくるメンバー（ニックネーム順）。
 * @graph-connects none
 */
export function memberOptions(feedbacks: readonly ProjectFeedbackSummary[]): FilterOption[] {
  const byEmail = new Map(feedbacks.map((f) => [f.email, f.nickname]));
  return [...byEmail.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * 一覧に出てくる案件（課題キー順）。
 * @graph-connects none
 */
export function projectOptions(feedbacks: readonly ProjectFeedbackSummary[]): FilterOption[] {
  const byKey = new Map(feedbacks.map((f) => [f.pjKey, f.projectTitle]));
  return [...byKey.entries()]
    .map(([value, title]) => ({ value, label: `${value} ${title}` }))
    .sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }));
}

/** 一覧画面の search param。 @graph-connects none */
export interface FeedbackListSearch {
  member?: string;
  pjKey?: string;
  objection?: boolean;
}

/**
 * 一覧画面の search param を検証する。形の合わない値は落とす。
 * @graph-connects ISSUE_KEY_PATTERN [references] 課題キーの形
 */
export function parseFeedbackListSearch(search: Record<string, unknown>): FeedbackListSearch {
  const result: FeedbackListSearch = {};
  const member = search.member;
  if (typeof member === 'string' && member.includes('@')) result.member = member.toLowerCase();
  const pjKey = search.pjKey;
  if (typeof pjKey === 'string' && ISSUE_KEY_PATTERN.test(pjKey)) result.pjKey = pjKey;
  if (search.objection === true || search.objection === 'true') result.objection = true;
  return result;
}

/** 詳細画面の search param。 @graph-connects none */
export interface FeedbackDetailSearch {
  version?: number;
}

/**
 * 詳細画面の search param を検証する。版は 1 以上の整数だけ。
 * @graph-connects none
 */
export function parseFeedbackDetailSearch(search: Record<string, unknown>): FeedbackDetailSearch {
  const version = Number(search.version);
  return Number.isInteger(version) && version >= 1 ? { version } : {};
}

/**
 * メールから表示名を引く関数を作る。ニックネームが無ければメールのローカル部。
 * @graph-connects none
 */
export function createNameResolver(nicknameByEmail: ReadonlyMap<string, string>) {
  return (email: string): string =>
    nicknameByEmail.get(email.toLowerCase()) ?? email.split('@')[0] ?? email;
}
